import { readCookieValue } from '../composables/api'

export const useVisitorSession = defineStore('visitorSession', {
  state: (): SessionData => {
    return {
      id: -1,
      user_id: -1,
      admin: false,
      email: '',
      reg_ip: '',
      money: 0,
      token: '',
      no_email: false,
      web_login: true,
      extra: {
      },
      billing: {
        firstname: '',
        lastname: '',
        addr: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        company_name: '',
        company_vat: '',
      },
      balance_currency: '',
      site_domain: '',
      created_at: '',
      updated_at: '',
      jwt_token: '',
      user_hash: '',
      riskRecord: null as highRiskUsersAttributes | null,
      viewAsCustomer: false,
      tokenOfThisSession: ''
    }
  },
  getters: {
    isAdmin(): boolean { return this.admin && !this.viewAsCustomer },
    realAdmin(): boolean { return this.admin },
    loggedIn(): boolean { return this.user_id !== -1 }
  },
  actions: {
    async getSession(reload = false) {
      // console.log('getSession: reload is', reload)
      // const jwtCookie = useCookie('jwt_token', {
      //   expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180),
      //   secure: useIsBrowser() ? location.protocol === 'https:' : false
      // })
      const jwtCookie = this.jwt_token || readCookieValue('jwt_token', null)
      // console.log('jwtCookie:', jwtCookie)
      if (!jwtCookie) {
        // console.log('jwt_token is empty, skip loading session')
        return
      }
      // if logged in already, do not re-fetch session data.
      // only if:
      // 1. reload is true
      // 2. jwt_token is different from the one in state/cookie
      if (!reload && this.loggedIn && this.tokenOfThisSession === jwtCookie) {
        // try {
        //   useGtag().gtag('set', { user_id: this.id })
        // } catch (e) {
        //   console.log('gtag set user failed:', e)
        // }
        return this
      }
      const data = await useAPI().get('/sessions', { key: 'session' }) as SessionData
      // console.log('api returned user.id:', data.id)
      const userId = data.user_id
      if (userId === -1) {
        // session invalid. clear cookies
        // console.log('[session invalid] clearing session cookies')
      }
      // session is valid, server returns {id: ... blabla.}
      if (this.id !== userId) {
        // console.log('calling_setSession')
        this.setSession(data)
        this.tokenOfThisSession = data.jwt_token
        // console.log('setSession_done')
      }
      // console.log('returning')
      return data
    },
    restore() {
      return this.getSession()
    },
    async login(postData: { email: string, password: string }) {
      // console.log('[useVisitorSession] login with:', postData)
      const data = await useAPI().post('/sessions', { body: postData }) as SessionData
      this.setSession(data)
      return data
    },
    logout(redirect = true) {
      // console.log('calling logout')
      const $intercom = useIntercom()
      // $utils.cookies.remove('jwt_token')
      const jwtCookie = useCookie('jwt_token', {
        expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180),
        secure: useIsBrowser() ? location.protocol === 'https:' : false
      })
      const newsReadCookie = useCookie('news_read')
      jwtCookie.value = null
      newsReadCookie.value = null
      $intercom.shutdown()
      $intercom.boot({app_id: 'qhptt90l'})
      if (redirect) {
        return navigateTo('/')
      }
      // if (window?.location?.href) {
      //   window.location.href = '/'
      // }
    },
    setSession(data: SessionData | null) {
      // console.log('setSession with user id:', data?.id)
      if (!data) {
        // console.log('setSession: data is null')
        return
      }
      // if (useIsBrowser()) {
      //   try {
      //     useGtag().gtag('set', { user_id: data.id })
      //   } catch (e) {
      //     console.log('gtag set user failed:', e)
      //   }
      // }
      for (const key in data) {
        // console.log('will set key:', key, 'to', data[key])
        // await wait(1000)
        this[key] = data[key]
      }
      // console.log('All set.')

      if (!useIsBrowser()) {
        return data
      }
      
      // server api will response with set-cookie, so we don't need to set it here
      // setting cookie in SSR mode is not working
      if (data.jwt_token) {
        // console.log('const jwtCookie')
        const jwtCookie = useCookie('jwt_token', {
          expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180),
          secure: useIsBrowser() ? location.protocol === 'https:' : false
        })
        // console.log('const tkCookie')
        const tkCookie = useCookie<boolean | string | null | undefined>('tk_suspend', {
          expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30),
          secure: useIsBrowser() ? location.protocol === 'https:' : false
        })
        // $utils.cookies.set('jwt_token', data.jwt_token)
        // console.log('going to set jwtCookie.value to', data.jwt_token)

        jwtCookie.value = data.jwt_token
        // console.log('tried to set jwtCookie to', data.jwt_token, 'and it is now', jwtCookie.value)
 
        // preserve tk_suspend state
        // $utils.cookies.set('tk_suspend', data.tk_suspend)
        // jwtCookie.value = data.jwt_token
        const supportSuspended = data.extra.tk_suspend === true
        tkCookie.value = supportSuspended
        // console.log('tried to set tkCookie to', supportSuspended, 'and it is now', tkCookie.value)
        if (supportSuspended) {
          // force to shutdown intercom if tk_supend
          useIntercom().shutdown()
          return data
        }
      }
      this.tryBootIntercom()
      return data
    },
    setToken(token: string) {
      // $utils.cookies.set('jwt_token', token)
      const jwtCookie = useCookie('jwt_token', {
        expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180),
        secure: useIsBrowser() ? location.protocol === 'https:' : false
      })
      jwtCookie.value = token
      this.jwt_token = token
    },
    switchAdminStatus() {
      this.viewAsCustomer = !this.viewAsCustomer
    },
    tryBootIntercom() {
      const tkCookie = useCookie<boolean | string | null | undefined>('tk_suspend', {
        expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30),
        secure: useIsBrowser() ? location.protocol === 'https:' : false
      })
      // not in browser, pass.
      // in browser? check if tkCookie is set (tkCookie is tk_suspend)
      // console.log('!useIsBrowser()', !useIsBrowser())
      // console.log('tkCookie.value', tkCookie.value)
      const supportSuspended = tkCookie.value === true || tkCookie.value === 'true'
      if (!useIsBrowser() || supportSuspended) {
        console.log('not in browser or tkCookie.value is true, skip booting intercom.')
        
        return false
      } 
      // console.log('booting intercom...')
      const $intercom = useIntercom()
      // console.log('trying to boot intercom with args:', this.loggedIn ? this.$state : {})
      try {
        // console.log('booting intercom with:', this.$state)
        $intercom.boot(this.loggedIn ? {
          app_id: 'qhptt90l',
          user_id: `${this.$state.id}`,
          email: this.$state.email,
          name: this.$state.email,
          user_hash: this.$state.user_hash
        } : {app_id: 'qhptt90l'})
      } catch (e) {
        console.error('boot intercom error:', e)
        return false
      }
      return true
    }
  },
})
